import React, { useState, useEffect, useCallback } from 'react';
import { getAllCompaniesForFeatureGroup, updateUserRole, updateUserCompany, removeUserCompany, removeUserFromFeatureGroup } from '../services/featureService';
import './EditUserPanel.css';

const EditUserPanel = ({ isOpen, user, featureGroupId, onClose, onSuccess }) => {
  const [role, setRole] = useState(user?.role || 'Guest');
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const loadCompanies = useCallback(async () => {
    if (!featureGroupId) return;
    try {
      const data = await getAllCompaniesForFeatureGroup(featureGroupId);
      setCompanies(data);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  }, [featureGroupId]);

  useEffect(() => {
    if (isOpen && featureGroupId) {
      loadCompanies();
      if (user) {
        setRole(user.role || 'Guest');
        // Find user's company ID if they have one
        // We'll need to get this from the user data or fetch it separately
        setCompanyId(user.company_id || '');
      }
    }
  }, [isOpen, featureGroupId, user, loadCompanies]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Update role
      await updateUserRole(user.user_id, featureGroupId, role);

      // Update company
      if (companyId) {
        await updateUserCompany(user.user_id, companyId, featureGroupId);
      } else {
        // Remove company if none selected
        await removeUserCompany(user.user_id, featureGroupId);
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to update user' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to remove ${user.email} from this feature map?`)) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await removeUserFromFeatureGroup(user.user_id, featureGroupId);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to remove user' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className={`edit-user-panel ${isOpen ? 'open' : ''}`}>
      <div className="panel-content">
        <div className="panel-header">
          <h2>Edit User</h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="panel-body">
          <div className="user-info-display">
            <p><strong>Email:</strong> {user.email}</p>
            {user.full_name && <p><strong>Name:</strong> {user.full_name}</p>}
          </div>

          <form onSubmit={handleSubmit} className="edit-user-form">
            <div className="form-group">
              <label htmlFor="role">
                Role <span className="required">*</span>
              </label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="Guest">Guest</option>
                <option value="Editor">Editor</option>
                <option value="Owner">Owner</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="companyId">Company</label>
              <select
                id="companyId"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                <option value="">No company assigned</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              <small className="help-text">
                Select a company to associate this user with, or leave blank to remove company association
              </small>
            </div>

            {errors.submit && (
              <div className="error-message submit-error">{errors.submit}</div>
            )}

            <div className="panel-actions">
              <button 
                type="button" 
                className="delete-button" 
                onClick={handleDelete}
                disabled={loading}
              >
                <i className="fas fa-trash"></i> Remove User
              </button>
              <div className="action-buttons">
                <button type="button" className="cancel-button" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="save-button" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className="panel-overlay" onClick={onClose}></div>
    </div>
  );
};

export default EditUserPanel;

